import React, { useState, useEffect, useMemo } from 'react';
import SEO from '../components/SEO';
import { useTheme } from '../context/ThemeContext';
import { api } from '../api';
import ChargingCurveChart from '../components/ChargingCurveChart';
import { summarizeMetrics } from '../lib/parseCurveCsv';

const carName = (c) => `${c.make} ${c.model}${c.trim ? ' ' + c.trim : ''}`.trim();
const socRange = (s) => {
  const a = s.metrics?.soc_start, b = s.metrics?.soc_end;
  return a !== undefined && b !== undefined ? `${a}–${b}%` : '';
};

function Ladekurver() {
  const { darkMode } = useTheme();
  const [cars, setCars] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selCarId, setSelCarId] = useState(null);
  const [selSessionId, setSelSessionId] = useState(null);
  const [sortBy, setSortBy] = useState('soc'); // 'soc' | 'charger'

  useEffect(() => {
    (async () => {
      try {
        const [carsRes, sesRes] = await Promise.all([
          api('/api/curve-cars'),
          api('/api/charging-curves')
        ]);
        const carsData = carsRes.ok ? await carsRes.json() : [];
        const sesData = sesRes.ok ? await sesRes.json() : [];
        // only show cars that actually have sessions
        const withSessions = carsData.filter((c) => (c.sessionCount ?? 0) > 0);
        setCars(withSessions);
        setSessions(sesData);
        if (withSessions.length) {
          // deep link from e.g. /ladestopp cards: /ladekurver?bil=Zeekr 7GT
          const wanted = (new URLSearchParams(window.location.search).get('bil') || '').trim().toLowerCase();
          const match = wanted && withSessions.find((c) => {
            const name = `${c.make} ${c.model}`.toLowerCase();
            return name.includes(wanted) || wanted.includes(name);
          });
          setSelCarId((match || withSessions[0]).id);
        }
      } catch {
        setCars([]); setSessions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sessionsForCar = useMemo(() => {
    const list = sessions.filter((s) => s.carId === selCarId);
    const cmp = sortBy === 'charger'
      ? (a, b) => a.charger.localeCompare(b.charger, 'no')
      : (a, b) => (a.metrics?.soc_start ?? 0) - (b.metrics?.soc_start ?? 0)
                  || (b.metrics?.soc_end ?? 0) - (a.metrics?.soc_end ?? 0);
    return [...list].sort(cmp);
  }, [sessions, selCarId, sortBy]);

  // keep a valid selected session whenever the car / sort changes
  useEffect(() => {
    if (!sessionsForCar.length) { setSelSessionId(null); return; }
    if (!sessionsForCar.some((s) => s.id === selSessionId)) {
      setSelSessionId(sessionsForCar[0].id);
    }
  }, [sessionsForCar, selSessionId]);

  const selected = sessions.find((s) => s.id === selSessionId) || null;
  const selCar = cars.find((c) => c.id === selCarId) || null;

  const bg = darkMode ? 'bg-slate-900' : 'bg-stone-50';
  const card = darkMode ? 'bg-slate-800 bg-opacity-60 border-slate-700' : 'bg-white border-stone-200';
  const heading = darkMode ? 'text-white' : 'text-stone-900';
  const sub = darkMode ? 'text-gray-400' : 'text-stone-500';
  const pill = (active) =>
    active
      ? 'bg-teal-500 text-slate-900 font-semibold'
      : darkMode
        ? 'bg-slate-800 text-gray-300 hover:bg-slate-700 hover:text-teal-400 border border-slate-700'
        : 'bg-white text-stone-600 hover:bg-stone-100 hover:text-teal-600 border border-stone-200';

  return (
    <div className={`min-h-screen ${bg} pt-8 pb-16 px-4`}>
      <SEO
        title="Ladekurver"
        description="Interaktive ladekurver – ladeeffekt (kW) og strøm (A) mot ladenivå for ulike elbiler, sortert på modell, lader og ladenivå."
        path="/ladekurver"
      />
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className={`text-3xl sm:text-4xl font-bold ${heading}`}>Ladekurver</h1>
          <p className={`mt-2 ${sub}`}>
            Interaktive ladekurver fra virkelige ladeøkter. Velg bil, så en ladeøkt for å
            se ladeeffekt og strøm mot ladenivå.
          </p>
        </div>

        {loading ? (
          <p className={sub}>Laster ladekurver…</p>
        ) : cars.length === 0 ? (
          <p className={sub}>Ingen ladekurver er lagt inn ennå.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
            {/* Car list (with images) */}
            <div>
              <h2 className={`text-xs uppercase tracking-wide font-semibold mb-3 ${sub}`}>Bil</h2>
              <div className="flex flex-col gap-2">
                {cars.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelCarId(c.id)}
                    className={`flex items-center gap-3 text-left p-2 rounded-xl transition-colors ${pill(c.id === selCarId)}`}
                  >
                    <CarThumb car={c} darkMode={darkMode} />
                    <span className="min-w-0">
                      <span className="block text-sm font-bold truncate">{c.make} {c.model}</span>
                      <span className="block text-xs opacity-80 truncate">
                        {c.trim || ' '} · {c.sessionCount} økt{c.sessionCount === 1 ? '' : 'er'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sessions + chart */}
            <div>
              {selCar && (
                <div className={`flex items-center gap-4 mb-4 rounded-2xl border ${card} p-4`}>
                  <CarThumb car={selCar} darkMode={darkMode} big />
                  <div>
                    <h2 className={`text-xl font-bold ${heading}`}>{carName(selCar)}</h2>
                    <p className={`text-sm ${sub}`}>
                      {sessionsForCar.length} ladeøkt{sessionsForCar.length === 1 ? '' : 'er'}
                    </p>
                  </div>
                </div>
              )}

              {/* Sort + session selector */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`text-xs uppercase tracking-wide ${sub} mr-1`}>Sorter:</span>
                <button onClick={() => setSortBy('soc')}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${pill(sortBy === 'soc')}`}>
                  Ladenivå
                </button>
                <button onClick={() => setSortBy('charger')}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${pill(sortBy === 'charger')}`}>
                  Lader
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {sessionsForCar.map((s) => (
                  <button key={s.id} onClick={() => setSelSessionId(s.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${pill(s.id === selSessionId)}`}>
                    <span className="font-semibold">{socRange(s)}</span>
                    <span className="opacity-80"> · {s.charger}</span>
                  </button>
                ))}
              </div>

              {selected && (
                <div className={`rounded-2xl border ${card} p-4 sm:p-6`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                    <h3 className={`text-lg font-bold ${heading}`}>
                      {selected.charger} · {socRange(selected)}
                    </h3>
                    {selected.videoUrl && (
                      <a href={selected.videoUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-400 hover:text-teal-300">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.8A1 1 0 004.8 3.6v12.8a1 1 0 001.5.9l10-6.4a1 1 0 000-1.7l-10-6.4z" />
                        </svg>
                        Se video
                      </a>
                    )}
                  </div>

                  <ChargingCurveChart curve={selected.curve} hasAmp={selected.hasAmp} darkMode={darkMode} />

                  <div className={`flex gap-5 text-sm mt-1 ${sub}`}>
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-4 h-1 rounded" style={{ background: '#2dd4bf' }} />
                      Ladeeffekt (kW)
                    </span>
                    {selected.hasAmp && (
                      <span className="flex items-center gap-2">
                        <span className="inline-block w-4 h-1 rounded" style={{ background: '#f59e0b' }} />
                        Strøm (A)
                      </span>
                    )}
                  </div>

                  {summarizeMetrics(selected.metrics).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {summarizeMetrics(selected.metrics).map((mm) => (
                        <div key={mm.label}
                          className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-slate-900 text-gray-300' : 'bg-stone-100 text-stone-700'}`}>
                          <span className={sub}>{mm.label}: </span>
                          <span className="font-semibold">{mm.value}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <dl className={`mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm ${heading}`}>
                    <Info label="Lader" value={selected.charger} darkMode={darkMode} />
                    {selected.cables && <Info label="Kabler" value={selected.cables} darkMode={darkMode} />}
                    {selected.notes && <Info label="Notater" value={selected.notes} darkMode={darkMode} full />}
                    {Object.entries(selected.extra || {}).map(([k, v]) => (
                      <Info key={k} label={k} value={String(v)} darkMode={darkMode} />
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CarThumb({ car, darkMode, big }) {
  const size = big ? 'w-20 h-20' : 'w-12 h-12';
  if (car.image) {
    return <img src={car.image} alt={carName(car)}
      className={`${size} rounded-lg object-cover flex-shrink-0`} />;
  }
  return (
    <div className={`${size} rounded-lg flex-shrink-0 flex items-center justify-center ${darkMode ? 'bg-slate-700 text-gray-500' : 'bg-stone-200 text-stone-400'}`}>
      <svg className="w-1/2 h-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M3 13l2-5a2 2 0 012-1.4h10A2 2 0 0119 8l2 5M5 13h14v4H5zM7 17v1M17 17v1" />
      </svg>
    </div>
  );
}

function Info({ label, value, darkMode, full }) {
  const sub = darkMode ? 'text-gray-400' : 'text-stone-500';
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className={`text-xs uppercase tracking-wide ${sub}`}>{label}</dt>
      <dd className="mt-0.5 whitespace-pre-wrap">{value}</dd>
    </div>
  );
}

export default Ladekurver;
